import React from 'react';
import { AlertCircle } from 'lucide-react';
import { Modal } from '../../../components/common/Modal';
import { useLanguage } from '../../../features/language/useLanguage';
import type { Member, Role } from '../../../services/memberService';
import { useMemberLabels } from './memberLabels';

interface EditMemberRoleModalProps {
  member: Member | null;
  value: string;
  error: string | null;
  assignableRoles: Role[];
  roles: Role[];
  onChange: (value: string) => void;
  onClose: () => void;
  onSubmit: () => void;
}

/** Modal for changing an existing member's role. */
export const EditMemberRoleModal: React.FC<EditMemberRoleModalProps> = ({
  member,
  value,
  error,
  assignableRoles,
  roles,
  onChange,
  onClose,
  onSubmit,
}) => {
  const { t } = useLanguage();
  const { roleLabel, roleDisplay } = useMemberLabels(roles);

  return (
    <Modal isOpen={!!member} onClose={onClose} title={t('settingsMember.modals.editRoleTitle')}>
      <div className="space-y-4" data-test-id="member-edit-role-modal">
        <p className="text-sm text-gray-500">
          {t('settingsMember.modals.editRoleDescription')}{' '}
          <span className="font-medium text-gray-900">{member?.full_name}</span>.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase mb-1">
              {t('settingsMember.modals.editRoleCurrentLabel')}
            </label>
            <div className="text-sm text-gray-900">{member ? roleDisplay(member.role) : ''}</div>
          </div>
          <div>
            <label htmlFor="edit-role-select" className="block text-xs font-medium text-gray-500 uppercase mb-1">
              {t('settingsMember.modals.editRoleNewLabel')}
            </label>
            <select
              id="edit-role-select"
              data-test-id="member-edit-role-select"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              className="block w-full pl-3 pr-10 py-2 text-sm border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 rounded-md border"
            >
              {assignableRoles.map((role) => (
                <option key={role.name} value={role.name}>
                  {role.display_name || roleLabel(role.name)}
                </option>
              ))}
            </select>
          </div>
        </div>
        <p className="text-xs text-gray-500">{t('settingsMember.modals.editRoleNote')}</p>
        {error && (
          <div className="flex items-start gap-2 text-sm text-red-600">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}
        <div className="flex justify-end gap-3 mt-6">
          <button
            type="button"
            className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            onClick={onClose}
          >
            {t('auth.common.cancel')}
          </button>
          <button
            type="button"
            data-test-id="member-edit-role-submit-button"
            disabled={!value || value === member?.role}
            className="px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={onSubmit}
          >
            {t('settingsMember.actions.updateRole')}
          </button>
        </div>
      </div>
    </Modal>
  );
};
